import React, { useState } from 'react';

import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import ActionLibraryInstancePrintTemplate from 'components/_print/_templates/ActionLibraryInstancePrintTemplate/ActionLibraryInstancePrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormButtons from 'components/FormButtons';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import ROLES from 'config/constants/ROLES';
import useIsOwner from 'hooks/useIsOwner/useIsOwner';
import usePermissions from 'hooks/usePermissions';
import action_library_messages from 'messages/action_library_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';

import DelegateInstanceDialog from '../DelegateInstanceDialog/DelegateInstanceDialog';

type Props = {
  canComplete: boolean;
  completeDisabledTooltip: string;
  instance: ActionLibraryInstance;
  onCompleteClick: () => void;
  onDelete: () => void;
  onInstanceUpdate?: () => void;
};

const ActionLibraryInstanceActions: React.FC<Props> = ({
  canComplete,
  completeDisabledTooltip,
  instance,
  onCompleteClick,
  onDelete,
  onInstanceUpdate,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const queryClient = useQueryClient();
  const { checkIsOwner } = useIsOwner();
  const [isOrgAssetAdmin] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);

  const isOwner = checkIsOwner(instance.owner);
  const canDelete = isOrgAssetAdmin || isOwner;

  const { mutate: deleteInstance, isLoading: deleteLoading } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
      enqueueSnackbar(t(action_library_messages.success.instance_deleted), { variant: 'success' });
      onDelete();
    },
    onError: () => {
      enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
    },
  });

  // TODO: remove if not needed
  // const handleEdit = () => {
  //   history.push(generatePath(PATHS.ACTION_LIBRARY_INSTANCE_EDIT, { id: instance.id }));
  // };

  const handleDelete = async () => {
    const confirmed = await showConfirmationModal({
      title: t(action_library_messages.delete_confirmation.title),
      body: t(action_library_messages.delete_confirmation.body),
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  const handleDelegateSuccess = () => {
    onInstanceUpdate?.();
  };

  return (
    <CenteredGrid width='lg' withoutPadding>
      <FormButtons inline={!isMobile}>
        {!canComplete ? (
          <Tooltip title={completeDisabledTooltip}>
            <span>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled
                icon={false}
                showLoader={false}
                startIcon={<CheckCircleOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.instance_actions.complete_instance)}
              </ColoredButton>
            </span>
          </Tooltip>
        ) : (
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={false}
            icon={false}
            onClick={onCompleteClick}
            showLoader={false}
            startIcon={<CheckCircleOutlinedIcon />}
            textLike={false}
            variant='outlined'
          >
            {t(action_library_messages.instance_actions.complete_instance)}
          </ColoredButton>
        )}
        <PrintComponent
          ctaRenderer={({ onPrint }) => (
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={false}
              icon={false}
              onClick={onPrint}
              showLoader={false}
              startIcon={<PrintOutlinedIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.actions.print)}
            </ColoredButton>
          )}
          documentTitle={`Action Instance: ${instance.title}`}
          templateComponent={<ActionLibraryInstancePrintTemplate instance={instance} />}
        />
        {isOrgAssetAdmin && (
          <Tooltip title={instance.status === 'completed' ? t(action_library_messages.instance_actions.delegate_completed_blocked) : ''}>
            <span>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled={instance.status === 'completed'}
                fullWidth
                icon={false}
                onClick={() => setDelegateDialogOpen(true)}
                showLoader={false}
                startIcon={<PeopleOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.instance_actions.delegate)}
              </ColoredButton>
            </span>
          </Tooltip>
        )}
        {/* TODO: remove if not needed */}
        {/* <ColoredButton
          className=''
          customColor='secondary'
          disabled={deleteLoading}
          icon={false}
          onClick={handleEdit}
          showLoader={false}
          startIcon={<EditOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.edit)}
        </ColoredButton> */}
        {canDelete && (
          <Tooltip title={instance.status === 'completed' ? t(action_library_messages.instance_actions.delegate_completed_blocked) : ''}>
            <span>
              <ColoredButton
                className=''
                customColor={instance.status === 'completed' ? 'secondary' : 'danger'}
                disabled={deleteLoading || instance.status === 'completed'}
                icon={false}
                onClick={handleDelete}
                showLoader={deleteLoading}
                startIcon={<DeleteOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.actions.delete)}
              </ColoredButton>
            </span>
          </Tooltip>
        )}
      </FormButtons>
      <DelegateInstanceDialog
        instance={instance}
        onClose={() => setDelegateDialogOpen(false)}
        onSuccess={handleDelegateSuccess}
        open={delegateDialogOpen}
      />
    </CenteredGrid>
  );
};

export default ActionLibraryInstanceActions;
